import { useMemo, useState } from "react";
import { Ban, BookOpen, Bug, Eye, Plus, StickyNote } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { PageLoader } from "@/components/ui/Spinner";
import {
  useDivisionsPicker,
  useEffectiveRuleset,
  useEngAronMeta,
  useInvestorsPicker,
  useKnowledgeEntries,
  useKnowledgeLifecycle,
  useProjectsPicker,
} from "../api/engAronApi";
import { KnowledgeEntryFormModal } from "../components/KnowledgeEntryFormModal";
import { ScopeBadge } from "../components/ScopeBadge";
import type { KnowledgeEntry, KnowledgeKind, KnowledgeMeta } from "../types";
import { useAuthStore } from "@/store/auth";

type Tab = "guideline" | "error" | "false_positive" | "note" | "pending";

const DOC_TYPE_LABEL: Record<string, string> = {
  pb: "PB",
  pw: "PW",
  wniosek: "Wniosek",
  zud: "ZUD",
  inne: "Inne",
};

function EntryCard({
  entry,
  meta,
  onEdit,
}: {
  entry: KnowledgeEntry;
  meta: KnowledgeMeta;
  onEdit: (e: KnowledgeEntry) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const accept = useKnowledgeLifecycle("accept");
  const reject = useKnowledgeLifecycle("reject");
  const archive = useKnowledgeLifecycle("archive");
  const user = useAuthStore((s) => s.user);

  const categoryLabel =
    meta.categories.find((c) => c.value === entry.category)?.label ?? entry.category;
  const canEdit =
    meta.is_approver || (entry.status === "pending" && entry.created_by === user?.id);
  const long = entry.body.length > 320;

  return (
    <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {entry.kind === "error" ? (
            <Bug size={15} className="text-orange-500 shrink-0" />
          ) : entry.kind === "false_positive" ? (
            <Ban size={15} className="text-blue-500 shrink-0" />
          ) : entry.kind === "note" ? (
            <StickyNote size={15} className="text-green-600 shrink-0" />
          ) : (
            <BookOpen size={15} className="text-[var(--color-text-muted)] shrink-0" />
          )}
          <h3 className="font-medium text-sm text-[var(--color-text-primary)] truncate">
            {entry.title}
          </h3>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {entry.status === "pending" && <Badge color="yellow">Oczekuje</Badge>}
          {entry.status === "rejected" && <Badge color="red">Odrzucony</Badge>}
          {entry.status === "archived" && <Badge color="gray">Archiwum</Badge>}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <ScopeBadge entry={entry} />
        {entry.kind !== "note" && <Badge color="gray">{categoryLabel}</Badge>}
        {entry.doc_types.map((dt) => (
          <Badge key={dt} color="blue">
            {DOC_TYPE_LABEL[dt] ?? dt}
          </Badge>
        ))}
        {entry.overrides_title && (
          <Badge color="orange">wyjątek od: {entry.overrides_title}</Badge>
        )}
      </div>

      <p
        className={`text-sm text-[var(--color-text-secondary)] whitespace-pre-wrap ${
          expanded || !long ? "" : "line-clamp-4"
        }`}
      >
        {entry.body}
      </p>
      {long && (
        <button
          type="button"
          className="text-xs text-[var(--color-primary)]"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "zwiń" : "pokaż całość"}
        </button>
      )}

      {entry.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {entry.attachments.map((att) =>
            att.file.url ? (
              <button
                key={att.id}
                type="button"
                onClick={() => setPreview(att.file.url)}
                title={att.caption || att.file.original_filename}
              >
                <img
                  src={att.file.url}
                  alt={att.caption || att.file.original_filename}
                  className="h-20 w-28 object-cover border border-[var(--color-border)] hover:opacity-80"
                />
              </button>
            ) : null,
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <span className="text-[11px] text-[var(--color-text-muted)]">
          {entry.created_by_name ?? "—"} ·{" "}
          {new Date(entry.created_at).toLocaleDateString("pl-PL")}
          {entry.why ? ` · ${entry.why}` : ""}
        </span>
        <div className="flex gap-1.5">
          {canEdit && (
            <Button size="sm" variant="ghost" onClick={() => onEdit(entry)}>
              Edytuj
            </Button>
          )}
          {meta.is_approver && entry.status === "pending" && (
            <>
              <Button
                size="sm"
                variant="secondary"
                loading={accept.isPending}
                onClick={() => accept.mutate(entry.id)}
              >
                Akceptuj
              </Button>
              <Button
                size="sm"
                variant="danger"
                loading={reject.isPending}
                onClick={() => reject.mutate(entry.id)}
              >
                Odrzuć
              </Button>
            </>
          )}
          {meta.is_approver && entry.status === "active" && (
            <Button
              size="sm"
              variant="ghost"
              loading={archive.isPending}
              onClick={() => archive.mutate(entry.id)}
            >
              Archiwizuj
            </Button>
          )}
        </div>
      </div>

      {preview && (
        <Modal open onClose={() => setPreview(null)} title="Screen" size="xl">
          <img src={preview} alt="Podgląd" className="max-w-full mx-auto" />
        </Modal>
      )}
    </div>
  );
}

function RulesetPreviewModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [project, setProject] = useState("");
  const [investor, setInvestor] = useState("");
  const [clientBranch, setClientBranch] = useState("");
  const [clientSubBranch, setClientSubBranch] = useState("");
  const [branch, setBranch] = useState("");
  const [docType, setDocType] = useState("");
  const meta = useEngAronMeta();
  const projects = useProjectsPicker("");
  const investors = useInvestorsPicker("");
  const divisions = useDivisionsPicker(investor);
  const subDivisionOptions = (divisions.data ?? []).find((d) => d.id === clientBranch)
    ?.sub_branches;
  // A project fully determines investor/oddział/rejon on the backend, so the
  // manual oddział/rejon pickers only make sense when previewing without a project.
  const params = {
    ...(project ? { project } : {}),
    ...(investor && !project ? { investor } : {}),
    ...(clientBranch && !project ? { client_branch: clientBranch } : {}),
    ...(clientSubBranch && !project ? { client_sub_branch: clientSubBranch } : {}),
    ...(branch ? { branch } : {}),
    ...(docType ? { doc_type: docType } : {}),
  };
  const ruleset = useEffectiveRuleset(params, open);

  return (
    <Modal open={open} onClose={onClose} title="Co zobaczy ENG-ARON" size="xl">
      <div className="space-y-3">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
          <SearchableSelect
            label="Projekt"
            value={project}
            onChange={(e) => setProject(e.target.value)}
            options={[
              { value: "", label: "— bez projektu —" },
              ...(projects.data ?? []).map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
          <SearchableSelect
            label="Inwestor"
            value={investor}
            onChange={(e) => {
              setInvestor(e.target.value);
              setClientBranch("");
              setClientSubBranch("");
            }}
            options={[
              { value: "", label: "— bez inwestora —" },
              ...(investors.data ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <SearchableSelect
            label="Oddział"
            value={clientBranch}
            onChange={(e) => {
              setClientBranch(e.target.value);
              setClientSubBranch("");
            }}
            options={[
              { value: "", label: "— bez oddziału —" },
              ...(divisions.data ?? []).map((d) => ({ value: d.id, label: d.display_name })),
            ]}
          />
          <SearchableSelect
            label="Pododdział / Rejon"
            value={clientSubBranch}
            onChange={(e) => setClientSubBranch(e.target.value)}
            options={[
              { value: "", label: "— bez rejonu —" },
              ...(subDivisionOptions ?? []).map((sb) => ({
                value: sb.id,
                label: sb.display_name,
              })),
            ]}
          />
          <Select
            label="Branża"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            options={[{ value: "", label: "— dowolna —" }, ...(meta.data?.branches ?? [])]}
          />
          <Select
            label="Typ opracowania"
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            options={[{ value: "", label: "— dowolny —" }, ...(meta.data?.doc_types ?? [])]}
          />
        </div>
        {ruleset.isLoading ? (
          <PageLoader />
        ) : (
          <>
            <p className="text-xs text-[var(--color-text-muted)]">
              {ruleset.data?.count ?? 0} wpisów · hash {ruleset.data?.hash.slice(0, 12)}…
            </p>
            <pre className="text-xs whitespace-pre-wrap bg-[var(--color-bg-elevated)] border border-[var(--color-border)] p-3 max-h-[50vh] overflow-y-auto">
              {ruleset.data?.text ?? ""}
            </pre>
          </>
        )}
      </div>
    </Modal>
  );
}

export function EngAronKnowledgePage() {
  const [tab, setTab] = useState<Tab>("guideline");
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("");
  const [branch, setBranch] = useState("");
  const [docType, setDocType] = useState("");
  const [category, setCategory] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<KnowledgeEntry | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const meta = useEngAronMeta();

  const filters = useMemo(
    () => ({
      ...(tab === "pending" ? { status: "pending" } : { kind: tab, status: "active" }),
      ...(q ? { q } : {}),
      ...(scope ? { scope } : {}),
      ...(branch ? { branch } : {}),
      ...(docType ? { doc_type: docType } : {}),
      ...(category ? { category } : {}),
    }),
    [tab, q, scope, branch, docType, category],
  );
  const entries = useKnowledgeEntries(filters);
  const pendingCount = useKnowledgeEntries({ status: "pending" });

  if (meta.isLoading || !meta.data) return <PageLoader />;

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: "guideline", label: "Wytyczne" },
    { key: "error", label: "Katalog błędów" },
    { key: "false_positive", label: "Fałszywe alarmy" },
    { key: "note", label: "Notatki" },
    { key: "pending", label: "Do akceptacji", count: pendingCount.data?.length ?? 0 },
  ];

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
            Baza wiedzy ENG-ARON
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            Wytyczne, katalog błędów i notatki — na tym ENG-ARON opiera recenzje i
            odpowiedzi. Poziomy: globalny → branża → inwestor → oddział → rejon →
            projekt (głębsze nadpisują płytsze).
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setPreviewOpen(true)}>
            <Eye size={14} className="mr-1.5" /> Co widzi AI
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus size={14} className="mr-1.5" />
            {meta.data.is_approver ? "Dodaj wpis" : "Zaproponuj wpis"}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--color-border)]">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-[var(--color-primary)] text-[var(--color-text-primary)] font-medium"
                : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            }`}
          >
            {t.label}
            {t.count ? (
              <span className="ml-1.5 text-xs bg-amber-100 text-amber-700 px-1.5 rounded-full">
                {t.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <Input placeholder="Szukaj…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          options={[{ value: "", label: "Poziom: wszystkie" }, ...meta.data.scopes]}
        />
        <Select
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
          options={[{ value: "", label: "Branża: wszystkie" }, ...meta.data.branches]}
        />
        <Select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          options={[{ value: "", label: "Typ: wszystkie" }, ...meta.data.doc_types]}
        />
        <Select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={[{ value: "", label: "Kategoria: wszystkie" }, ...meta.data.categories]}
        />
      </div>

      {/* List */}
      {entries.isLoading ? (
        <PageLoader />
      ) : (entries.data ?? []).length === 0 ? (
        <div className="text-center text-sm text-[var(--color-text-muted)] py-12">
          {tab === "pending"
            ? "Brak wpisów oczekujących na akceptację."
            : "Brak wpisów — dodaj pierwszy."}
        </div>
      ) : (
        <div className="space-y-3">
          {(entries.data ?? []).map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              meta={meta.data}
              onEdit={(e) => {
                setEditing(e);
                setFormOpen(true);
              }}
            />
          ))}
        </div>
      )}

      <KnowledgeEntryFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        meta={meta.data}
        entry={editing}
        defaultKind={(tab === "pending" ? "guideline" : tab) as KnowledgeKind}
      />
      <RulesetPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </div>
  );
}
